// Shape of `Job.output` for `PROCESS_AMAZON_WADM` jobs (ENG-15148).
//
// Single source of truth for the Lambda that produces the summary and the Oak
// frontend that renders it. Both import from here so the shape can't drift.

export type CupaBreakdownEntry = {
  lepcName: string | null;
  county: string | null;
  reportCount: number;
};

export type InventoryChangeDetail = {
  facilityId: string;
  facilityName: string | null;
  chemicalId: string;
  chemicalName: string | null;
  kind: "created" | "updated" | "unchanged";
  priorMaxAmount: number | null;
  newMaxAmount: number;
  priorMaxAmountCode: number | null;
  newMaxAmountCode: number | null;
  priorAverageAmount: number | null;
  /** Mirrors `newMaxAmount` today — a single WADM cycle is a point-in-time snapshot. */
  newAverageAmount: number;
  priorAverageAmountCode: number | null;
  newAverageAmountCode: number | null;
};

/** Why a facility's rows were skipped this run (instead of aborting the whole run). */
export type BlockedFacilityReason =
  | "unmatchedProfileCode"
  | "ambiguousChemical"
  | "missingChemical";

export const BLOCKED_FACILITY_REASONS: readonly BlockedFacilityReason[] = [
  "unmatchedProfileCode",
  "ambiguousChemical",
  "missingChemical",
];

export type BlockedFacility = {
  /** Amazon siteCode (normalized) whose rows were skipped. */
  siteCode: string;
  /** The matched facility, when the siteCode resolved to one; null otherwise. */
  facilityId: string | null;
  /** Deduped, sorted reasons the facility was skipped. */
  reasons: BlockedFacilityReason[];
};

export type RunSummary = {
  wadmCycleId: string;
  /** Total distinct siteCodes the run looked at (matched + out-of-state + unmatched). */
  facilitiesAttempted: number;
  /** Distinct siteCodes that resolved to a CA facility. The denominator for inventory/report work. */
  facilitiesMatched: number;
  itemsUpdated: number;
  /**
   * Facilities with an open UPDATE for this reporting year after the run —
   * sums drafts opened this run and drafts that were already open from a
   * prior cycle (those facilities appear in `facilityIdsWithExistingOpenUpdate`).
   */
  reportsCreated: number;
  /**
   * Facility ids skipped this run because an open UPDATE already existed for
   * the facility+reportingYear. Includes same-cycle retries and cross-cycle
   * pile-up candidates. Sorted.
   */
  facilityIdsWithExistingOpenUpdate: string[];
  unmatchedProfileCodes: string[];
  /**
   * Recognized-but-intentionally-unreported profile codes dropped this run (see
   * `WADM_EXCLUDED_PROFILES`). Unlike `unmatchedProfileCodes`, these do NOT
   * block their facility. Optional: `Job.output` records written before this
   * field existed won't carry it, so consumers should treat absence as `[]`.
   */
  excludedProfileCodes?: string[];
  missingChemicals: string[];
  unmatchedSiteCodes: string[];
  /**
   * Facilities whose rows were skipped this run because at least one of their
   * rows could not be mapped (unmatched profile code, or an ambiguous/missing
   * target chemical). Clean facilities are still written; the run then ends in
   * FAILED so the gap is surfaced, with this list naming what to fix. Sorted by
   * siteCode. Optional: `Job.output` records written before this field existed
   * won't carry it, so consumers should treat absence as `[]`.
   */
  blockedFacilities?: BlockedFacility[];
  cupaBreakdown: CupaBreakdownEntry[];
  /** Per-row detail for every FacilityChemical created/updated this run. `unchanged` rows are not included. */
  inventoryChanges: InventoryChangeDetail[];
};

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function isStringArray(v: unknown): v is string[] {
  return Array.isArray(v) && v.every((x) => typeof x === "string");
}

function isNullableString(v: unknown): v is string | null {
  return v === null || typeof v === "string";
}

function isNullableNumber(v: unknown): v is number | null {
  return v === null || typeof v === "number";
}

function isCupaBreakdownEntry(v: unknown): v is CupaBreakdownEntry {
  if (!isPlainObject(v)) return false;
  if (!isNullableString(v.lepcName)) return false;
  if (!isNullableString(v.county)) return false;
  if (typeof v.reportCount !== "number") return false;
  return true;
}

function isInventoryChangeKind(v: unknown): v is InventoryChangeDetail["kind"] {
  return v === "created" || v === "updated" || v === "unchanged";
}

function isBlockedFacilityReason(v: unknown): v is BlockedFacilityReason {
  return typeof v === "string" && BLOCKED_FACILITY_REASONS.includes(v as BlockedFacilityReason);
}

function isBlockedFacility(v: unknown): v is BlockedFacility {
  if (!isPlainObject(v)) return false;
  if (typeof v.siteCode !== "string") return false;
  if (!isNullableString(v.facilityId)) return false;
  if (!Array.isArray(v.reasons)) return false;
  if (!v.reasons.every(isBlockedFacilityReason)) return false;
  return true;
}

function isInventoryChangeDetail(v: unknown): v is InventoryChangeDetail {
  if (!isPlainObject(v)) return false;
  if (typeof v.facilityId !== "string") return false;
  if (!isNullableString(v.facilityName)) return false;
  if (typeof v.chemicalId !== "string") return false;
  if (!isNullableString(v.chemicalName)) return false;
  if (!isInventoryChangeKind(v.kind)) return false;
  if (!isNullableNumber(v.priorMaxAmount)) return false;
  if (typeof v.newMaxAmount !== "number") return false;
  if (!isNullableNumber(v.priorMaxAmountCode)) return false;
  if (!isNullableNumber(v.newMaxAmountCode)) return false;
  if (!isNullableNumber(v.priorAverageAmount)) return false;
  if (typeof v.newAverageAmount !== "number") return false;
  if (!isNullableNumber(v.priorAverageAmountCode)) return false;
  if (!isNullableNumber(v.newAverageAmountCode)) return false;
  return true;
}

/**
 * Narrow a loosely-typed value (e.g. `Job.output` from the GraphQL `JSON`
 * scalar) into a `RunSummary` without an `as` cast. Returns `true` only when
 * every top-level field is present with the right kind AND every entry in
 * `cupaBreakdown` / `inventoryChanges` matches the expected shape. An upstream
 * code change that drops a field will fail this check up-front rather than
 * silently render empty cells in the customer-facing table.
 */
export function isWadmRunSummary(value: unknown): value is RunSummary {
  if (!isPlainObject(value)) return false;
  if (typeof value.wadmCycleId !== "string") return false;
  if (typeof value.facilitiesAttempted !== "number") return false;
  if (typeof value.facilitiesMatched !== "number") return false;
  if (typeof value.itemsUpdated !== "number") return false;
  if (typeof value.reportsCreated !== "number") return false;
  if (!isStringArray(value.facilityIdsWithExistingOpenUpdate)) return false;
  if (!isStringArray(value.unmatchedProfileCodes)) return false;
  // Optional — historical Job.output records predate this field. If present it
  // must be a string array; if absent the summary is still valid.
  if (value.excludedProfileCodes !== undefined && !isStringArray(value.excludedProfileCodes))
    return false;
  if (!isStringArray(value.missingChemicals)) return false;
  if (!isStringArray(value.unmatchedSiteCodes)) return false;
  // Optional — historical Job.output records predate this field. If present it
  // must be an array of valid BlockedFacility entries; if absent the summary is
  // still valid (consumers treat absence as []).
  if (
    value.blockedFacilities !== undefined &&
    (!Array.isArray(value.blockedFacilities) || !value.blockedFacilities.every(isBlockedFacility))
  ) {
    return false;
  }
  if (!Array.isArray(value.cupaBreakdown)) return false;
  if (!value.cupaBreakdown.every(isCupaBreakdownEntry)) return false;
  if (!Array.isArray(value.inventoryChanges)) return false;
  if (!value.inventoryChanges.every(isInventoryChangeDetail)) return false;
  return true;
}
